import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateBlogTemplateCategories } from '../Admin/Template/useAdminBlogTemplateCategories';
import { invalidateAllHwcIndustryCategoryTemplates } from '../Admin/Template/useHwcIndustryCategoryTemplates';
import { invalidateEmailAllCategoryTemplates } from '../EmailCampaign/useEmailAllCategoryTemplates';
import { invalidateAllTemplateCategorySocialQueries } from './useTemplateCategorySocialQuery';

/** Industry-scoped social templates have no per-platform surface, and the typed routes refuse them. */
const isSocialTemplate = (template: Api.ITemplate) =>
	template?.templateType === Api.TemplateType.SocialMediaPost && template?.scope !== Api.TemplateScope.Industry;

interface IParams {
	template: Api.ITemplate | Api.ISocialMediaPostTemplate;
	forUserId?: string;
	impersonationContext?: Api.IImpersonationContext;
}

export function useTemplateCreateMutation({
	onSuccess,
	onError,
}: {
	onSuccess?: (data: Api.ITemplate, variables: IParams) => void;
	onError?: (error: Api.IOperationResultNoValue) => void;
} = {}) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({ template, forUserId, impersonationContext }: IParams) =>
			userSession.webServiceHelper.callAsync<Api.ITemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					// The generic route binds the base model, so a social template's per-platform versions
					// would be dropped on the way in. Only the typed route stores them.
					urlPath: isSocialTemplate(template) ? `template/social` : `template`,
					queryParams: {
						forUserId,
					},
					impersonationContext,
				}),
				`POST`,
				template
			),
		onError,
		onSuccess: (data, variables) => {
			invalidateAllHwcIndustryCategoryTemplates();
			invalidateBlogTemplateCategories();
			invalidateAllTemplateCategorySocialQueries();
			invalidateEmailAllCategoryTemplates();

			// Call the original onSuccess callback if provided
			onSuccess?.(data, variables);
		},
	});
}
